/**
 * RolloutStrip — console-style rollout picker: one rounded block per
 * rollout, grouped and colored by model, block height scaled by that
 * rollout's score so the spread reads at a glance. Clicking a block
 * selects its (model, rollout); the selected block wears a dark ring.
 * A caption line under the strip names the selection.
 */

import { formatScore } from '@/components/evals/format'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { modelLogoSrc } from './benchmarkAnalysis'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { RolloutEntry } from '@/api/generated/model'

/** Per-model block tones, assigned by group order (console palette). */
const STRIP_TONES = [
  'hsl(205 60% 72%)', // pastel sky
  'hsl(255 55% 78%)', // pastel violet
  'hsl(38 70% 68%)', // pastel amber
  'hsl(150 35% 65%)', // pastel green
  'hsl(340 45% 76%)', // pastel rose
]

const BLOCK_MIN_PX = 10
const BLOCK_MAX_PX = 40

function blockHeight(score: number | null): number {
  if (score === null) return BLOCK_MIN_PX
  return BLOCK_MIN_PX + score * (BLOCK_MAX_PX - BLOCK_MIN_PX)
}

interface ModelGroup {
  model: string
  tone: string
  entries: RolloutEntry[]
}

function groupByModel(rollouts: RolloutEntry[]): ModelGroup[] {
  const groups: ModelGroup[] = []
  for (const entry of rollouts) {
    const group = groups.find((g) => g.model === entry.model)
    if (group === undefined) {
      groups.push({
        model: entry.model,
        tone: STRIP_TONES[groups.length % STRIP_TONES.length] ?? STRIP_TONES[0] ?? '',
        entries: [entry],
      })
    } else {
      group.entries.push(entry)
    }
  }
  return groups
}

export function RolloutStrip({
  rollouts,
  selected,
  onSelect,
  caption,
}: {
  rollouts: RolloutEntry[]
  /** The selected (model, rollout id) pair. */
  selected: { model: string; rolloutId: string } | undefined
  onSelect: (entry: RolloutEntry) => void
  /** Extra caption detail for the loaded rollout (e.g. message count). */
  caption?: string
}): React.JSX.Element {
  const groups = groupByModel(rollouts)
  const selectedEntry = rollouts.find(
    (entry) => entry.model === selected?.model && entry.id === selected.rolloutId
  )

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Rollouts · {rollouts.length}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-end gap-4">
        {groups.map((group) => (
          <div key={group.model} className="flex flex-wrap items-end gap-1.5">
            {group.entries.map((entry) => {
              const active = entry === selectedEntry
              return (
                <Tooltip key={entry.id}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-pressed={active}
                      aria-label={`${entry.model} ${entry.id} · ${formatScore(entry.score)}`}
                      onClick={() => {
                        onSelect(entry)
                      }}
                      className={cn(
                        'w-9 rounded-lg transition-[height,box-shadow] hover:opacity-90',
                        active && 'ring-2 ring-foreground'
                      )}
                      style={{ height: blockHeight(entry.score), backgroundColor: group.tone }}
                    />
                  </TooltipTrigger>
                  <TooltipContent className="text-center">
                    <p className="font-mono text-xs">
                      {entry.model} · {formatScore(entry.score)}
                    </p>
                    <p className="text-xs text-muted-foreground">click to view the trace</p>
                  </TooltipContent>
                </Tooltip>
              )
            })}
          </div>
        ))}
      </div>
        {selectedEntry !== undefined && (
          <p className="flex items-center gap-1.5 font-mono text-xs text-muted-foreground">
          {(() => {
            const logo = modelLogoSrc(selectedEntry.model)
            return logo !== null ? (
              <img src={logo} alt="" className="size-3.5 object-contain" />
            ) : null
          })()}
          <span className="text-foreground">{selectedEntry.model}</span>
          <span>· {formatScore(selectedEntry.score)}</span>
            {caption !== undefined && <span>· {caption}</span>}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
